import { cloneDeep, range } from 'lodash';
import type { LiteralUnion } from 'type-fest';
import { v4 as uuidv4 } from 'uuid';
import { getMaterialAssets } from '@/i18n/assets';
import {
    LayerType,
    type BaseCells,
    type Border,
    type Cell,
    type CellStyle,
    type ITableLayerProps,
    type PureCell,
} from '@/version-types';
import type { TablePreset } from '.';
import { DEFAULT_TABLE_CELL__BACKGROUND_COLOR } from '../default-configs';
import presetImage from './assets/preset-common.png';
import commonTableConstants from './common-table-constants';

const PRESET_ID = 'da05c556';

export type TableConstants = {
    readonly borderWidth: number;
    readonly borderColor: string;
    readonly cellWidth: number;
    readonly cellHeight: number;
};

type FormulaCellProp = ITableLayerProps<true>['cells'];

export const getTableByCommonPreset = (initData?: {
    cells: FormulaCellProp;
    baseCells: BaseCells;
    styles: Record<LiteralUnion<'1', string>, CellStyle>;
    tableConstants?: Partial<TableConstants>;
}): ReturnType<TablePreset['create']> => {
    const { borderWidth, borderColor, cellWidth, cellHeight } = {
        ...commonTableConstants,
        ...initData?.tableConstants,
    };
    const getBorder = (): Border => ({
        width: borderWidth,
        style: 'line-0',
        color: borderColor as `#${string}`,
    });
    const allStyle: CellStyle = {
        fontSize: 30,
        border: {
            top: getBorder(),
            right: getBorder(),
            bottom: getBorder(),
            left: getBorder(),
        },
    };
    const defaultHCount = 4;
    const defaultVCount = 4;
    const defaultCells = (() => {
        const cellsProp: FormulaCellProp = range(defaultVCount).map(() => Array(defaultHCount).fill(null));
        for (let vIndex = 0; vIndex < defaultVCount; vIndex++) {
            for (let hIndex = 0; hIndex < defaultHCount; hIndex++) {
                const cell: PureCell = {
                    vIndex,
                    hIndex,
                    colSpan: 1,
                    rowSpan: 1,
                    content: '',
                    styleIds: [],
                };
                cellsProp[vIndex][hIndex] = cell;
            }
        }
        return cellsProp;
    })();
    const defaultBaseCells: BaseCells = {
        horizontal: Array(defaultHCount).fill(cellWidth),
        vertical: Array(defaultVCount).fill(cellHeight),
    };
    const defaultTableWidth = cellWidth * defaultHCount + borderWidth;
    const defaultTableHeight = cellHeight * defaultVCount + borderWidth;

    const baseCells = initData?.baseCells ?? defaultBaseCells;

    return {
        id: uuidv4(),
        layerType: LayerType.TABLE,
        width: initData
            ? initData.baseCells.horizontal.reduce((total, size) => total + size, 0) + borderWidth
            : defaultTableWidth,
        height: initData
            ? initData.baseCells.vertical.reduce((total, size) => total + size, 0) + borderWidth
            : defaultTableHeight,
        rotate: 0,
        opacity: 1,
        title: '',
        extraInfo: {
            lockRatio: false,
        },
        data: {
            presetId: PRESET_ID,
            screenshotUrl: initData ? undefined : getMaterialAssets().tableLayer.screenshotUrl.table1,
            cells: initData?.cells ?? defaultCells,
            baseCells,
            allStyleId: '1',
            styles: initData?.styles ?? {
                '1': allStyle,
            },
        },
        lock: false,
        children: [],
        shine: true,
        hidden: false,
        fitWidth: 0,
        fitHeight: 0,
    };
};

const tablePreset: TablePreset = {
    id: PRESET_ID,
    preview: presetImage,
    create: () => getTableByCommonPreset(),
    overwriteStyle: (oldTableLayer) => {
        const newTableLayer = cloneDeep(oldTableLayer);

        const newStyleId = `${Math.max(0, ...Object.keys(newTableLayer.data.styles).map(Number)) + 1}`;
        const cells = oldTableLayer.data.cells.flat().filter(Boolean) as Cell[];
        cells.forEach((c) => {
            c.styleIds.push(newStyleId);
        });
        const getBorder = (): Border => ({
            width: commonTableConstants.borderWidth,
            style: 'line-0',
            color: commonTableConstants.borderColor as `#${string}`,
        });
        newTableLayer.data.styles[newStyleId] = {
            backgroundColor: DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
            border: {
                top: getBorder(),
                right: getBorder(),
                bottom: getBorder(),
                left: getBorder(),
            },
        };

        return newTableLayer;
    },
};

export default tablePreset;
